import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Download, LogOut, RefreshCw, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'pl16_admin_token';

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)] flex items-center justify-center container-px">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-[380px] bg-white border border-[var(--line)] rounded-[14px] p-8 shadow-xl"
      >
        <p className="text-[11px] uppercase tracking-kicker text-[var(--muted)]">PL16 · Admin</p>
        <h1 className="mt-2 font-display uppercase font-tight text-3xl tracking-wide">Waitlist</h1>
        <p className="mt-2 text-sm text-[var(--muted)]">Sign in to view your drop signups.</p>

        <div className="mt-6 flex flex-col gap-3">
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="h-12 px-4 rounded-md border border-[var(--line)] text-base focus:outline-none focus:border-[var(--accent)]"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="h-12 px-4 rounded-md border border-[var(--line)] text-base focus:outline-none focus:border-[var(--accent)]"
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="btn-primary h-12 inline-flex items-center justify-center gap-2 text-[13px] uppercase tracking-[0.14em] disabled:opacity-60"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : 'Sign in'}
          </button>
        </div>
      </form>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/waitlist/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      setEntries(data.entries || []);
    } catch {
      toast.error('Could not load signups');
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const rows = [['email', 'source', 'created_at'], ...entries.map((e) => [e.email, e.source, e.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lotus-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[var(--paper)] py-10 container-px" data-testid="admin-dashboard">
      <div className="max-w-[900px] mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-kicker text-[var(--muted)]">PL16 · Admin</p>
            <h1 className="mt-1 font-display uppercase font-tight text-3xl tracking-wide">Drop Waitlist</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-button" className="btn-ghost inline-flex items-center gap-2 px-4 py-2.5 text-[12px] uppercase tracking-[0.12em]">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={downloadCsv} data-testid="admin-download-button" disabled={!entries.length} className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-[12px] uppercase tracking-[0.12em] disabled:opacity-50">
              <Download size={14} /> Download CSV
            </button>
            <button onClick={onLogout} data-testid="admin-logout-button" className="btn-ghost inline-flex items-center gap-2 px-4 py-2.5 text-[12px] uppercase tracking-[0.12em]">
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-[var(--line)] rounded-[12px] p-5">
            <p className="text-[11px] uppercase tracking-[0.14em] text-[var(--muted)]">Total signups</p>
            <p data-testid="admin-total-count" className="mt-1 font-display text-4xl tabular-nums">{entries.length}</p>
          </div>
        </div>

        <div className="mt-6 bg-white border border-[var(--line)] rounded-[12px] overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-[var(--muted)]"><Loader2 className="animate-spin" /></div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-[var(--muted)]">
              <Mail size={28} className="opacity-40" />
              <p className="mt-3 text-sm">No signups yet.</p>
            </div>
          ) : (
            <table className="w-full text-left" data-testid="admin-signups-table">
              <thead>
                <tr className="border-b border-[var(--line)] text-[11px] uppercase tracking-[0.12em] text-[var(--muted)]">
                  <th className="px-5 py-3 font-medium">#</th>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Source</th>
                  <th className="px-5 py-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={e.id || i} className="border-b border-[var(--line)] last:border-0 text-sm">
                    <td className="px-5 py-3 text-[var(--muted-2)] tabular-nums">{entries.length - i}</td>
                    <td className="px-5 py-3 text-[var(--ink)]">{e.email}</td>
                    <td className="px-5 py-3 text-[var(--muted)] capitalize">{e.source}</td>
                    <td className="px-5 py-3 text-[var(--muted)]">{fmtDate(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  if (!token) return <LoginView onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
